import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useMemo, useRef, useState } from 'react';
import { compact } from 'lodash-es';
import { Controller, useFieldArray, useForm, FormProvider } from 'react-hook-form';
import { Button, Callout, FullPageModal, useOpenUnsavedChangesModal, useStateInRef, Separator } from 'seagull';
import { amendmentsTextsPath } from '~/business-logic/amendmentsLogic';
import { useStore, useStoreActions } from '~/contexts';
import { useLegalTerms } from '~/hooks/reactQuery/queries/useLegalTerms';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useOnMount } from '~/hooks/useOnMount';
import { useUpdateRelationship } from '~/hooks/useUpdateRelationship';
import { useLocaleKeys } from '~/i18n';
import { handlePromise, scrollIntoView } from '~/infra/utils';
import { agreementChangeModel, relationshipModel, serviceModel } from '~/models';
import { AgreementDataModel } from '~/models/AgreementDataModel';
import { isCustomPageContentInvalid, toCustomPageDTO } from '~/models/custom-page/model';
import { useValidateService } from '~/models/service/hooks/useValidateService';
import { fromDateStampObject } from '~/models/time';
import { useAppConfig } from '~/store/store-context';
import { helpArticles } from '~/utils/helpArticles';
import { makeSentryIgnoredError } from '../ErrorBoundaryPage';
import ExternalLinkButton from '../ExternalLinkButton';
import { ServicesListStructure } from '../ServicesListStructure/ServicesListStructure';
import { EditAllowManualPayments, EditAmendmentsApprovalMethod, EditCCFees, EditInvoicesApprovalMethod, EditNetTerms, Section } from './EditAgreementComponents';
import { EditAgreementCustomPages } from './EditAgreementCustomPages/EditAgreementCustomPages';
import styles from './EditAgreementModal.module.scss';
import EditAgreementService from './EditAgreementService/EditAgreementService';
import { EditLegalTerms } from './EditLegalTerms/EditLegalTerms';
import { getApprovalCalloutText, makeServiceQboClassChanges, willEmailBeSent } from './utils/EditAgreementModal.utils';
export const autoApprovalHelpUrl = helpArticles.amendmentsRequiringApproval();
/**
 * @deprecated Use `useTestIds` instead
 */ export const getTestPrefix = (idx)=>"service-".concat(idx);
export default function EditAgreementModal(props) {
    useLegalTerms({
        refreshOnHookMount: true
    });
    const { relationship, dismissModal, registerCanDismiss, onUpdate } = props;
    const { logBI, fetchLegalTerms, fetchQboClasses } = useStoreActions();
    const { SERVICE_TEMPLATE_BUNDLE = false, CUSTOM_PAGES_AMENDMENTS = false } = useFeatureFlags();
    const { t: amendmentsTexts } = useLocaleKeys(amendmentsTextsPath);
    const { t: editAgreementTexts } = useLocaleKeys('components.editAgreementModal');
    const qboClasses = useStore((state)=>state.qboClasses.data);
    const { disableScrollAnimation } = useAppConfig();
    const openUnsavedChangesModal = useOpenUnsavedChangesModal();
    const updateRelationship = useUpdateRelationship();
    const approvalCalloutText = getApprovalCalloutText(relationship.amendmentsApprovalMethod);
    const onClose = ()=>dismissModal();
    const { isInvalid } = useValidateService();
    const { error: legalTermsError } = useStore((state)=>state.legalTerms);
    const servicesRef = useRef({});
    const scrollToServiceCounter = useRef(1);
    const originalServicesMap = useMemo(()=>new Map(relationship.services.map((s)=>[
                s.id,
                s
            ])), [
        relationship.services
    ]);
    const initialCustomPages = useMemo(()=>{
        var _relationship_customPages;
        return (_relationship_customPages = relationship.customPages) !== null && _relationship_customPages !== void 0 ? _relationship_customPages : [];
    }, [
        relationship.customPages
    ]);
    const [isCustomPageUploading, setIsCustomPageUploading] = useState(false);
    const [submittedCustomPageIds, setSubmittedCustomPageIds] = useState(new Set());
    const customPagesRef = useRef(null);
    const form = useForm({
        mode: 'all',
        defaultValues: {
            updatedNetTerms: relationshipModel.actualNetTerms(relationship),
            services: relationship.services,
            selectedLegalTerms: relationship.legalTermsDocuments,
            amendmentsApprovalMethod: relationshipModel.actualAmendmentsApprovalMethod(relationship),
            invoicesApprovalMethod: relationshipModel.actualInvoicesApprovalMethod(relationship),
            allowManualPayments: relationshipModel.actualAllowManualPayments(relationship),
            coverCreditCardFees: relationshipModel.actualIsVendorCoveringCCFee(relationship),
            customPages: initialCustomPages
        }
    });
    const { control, formState, watch, handleSubmit, getValues, trigger } = form;
    const { append, remove, update } = useFieldArray({
        control,
        name: 'services'
    });
    const [updatedNetTerms, services, selectedLegalTerms, amendmentsApprovalMethod, invoicesApprovalMethod, allowManualPayments, coverCreditCardFees, customPages] = watch([
        'updatedNetTerms',
        'services',
        'selectedLegalTerms',
        'amendmentsApprovalMethod',
        'invoicesApprovalMethod',
        'allowManualPayments',
        'coverCreditCardFees',
        'customPages'
    ]);
    const customPagesAreDirty = formState.dirtyFields.customPages != null;
    const fullCustomPagesForSubmit = useMemo(()=>customPages.map(toCustomPageDTO), [
        customPages
    ]);
    const isSelectedLegalTermsDirty = formState.dirtyFields.selectedLegalTerms === true;
    useOnMount(()=>{
        fetchLegalTerms();
        fetchQboClasses();
    });
    const submitText = useMemo(()=>{
        return willEmailBeSent({
            services,
            originalServicesMap,
            relationship,
            updatedNetTerms,
            isSelectedLegalTermsDirty,
            amendmentsApprovalMethod,
            invoicesApprovalMethod,
            allowManualPayments,
            coverCreditCardFees,
            serviceTemplateBundleEnabled: SERVICE_TEMPLATE_BUNDLE,
            texts: amendmentsTexts
        }) ? 'Save changes and notify client' : 'Save changes';
    }, [
        services,
        originalServicesMap,
        relationship,
        updatedNetTerms,
        isSelectedLegalTermsDirty,
        amendmentsApprovalMethod,
        invoicesApprovalMethod,
        allowManualPayments,
        coverCreditCardFees,
        SERVICE_TEMPLATE_BUNDLE,
        amendmentsTexts
    ]);
    const agreementData = useMemo(()=>AgreementDataModel.fromRelationship(relationship).setNetTerms(updatedNetTerms), [
        relationship,
        updatedNetTerms
    ]);
    registerCanDismiss(()=>formState.isDirty ? openUnsavedChangesModal({
            testId: 'edit-agr-confirm-cancel'
        }) : Promise.resolve(true));
    function onSubmit() {
        logBI(11001);
        updateRelationship(_object_spread_props(_object_spread({}, relationship), {
            changes: compact([
                agreementChangeModel.makeServicesChange(services.map((s)=>{
                    const withChanges = serviceModel.applyChanges(s);
                    return serviceModel.toDBService(withChanges, {
                        note: serviceModel.note(s)
                    });
                })),
                formState.dirtyFields.amendmentsApprovalMethod && agreementChangeModel.makeAmendmentsApprovalMethodChange(amendmentsApprovalMethod),
                formState.dirtyFields.invoicesApprovalMethod && agreementChangeModel.makeInvoicesApprovalMethodChange(invoicesApprovalMethod),
                formState.dirtyFields.updatedNetTerms && agreementChangeModel.makeNetTermsChange(updatedNetTerms),
                formState.dirtyFields.selectedLegalTerms && selectedLegalTerms && // this is safe because we validate the selectedLegalTerms in the EditLegalTerms controller level
                agreementChangeModel.makeLegalTermsChange(selectedLegalTerms),
                formState.dirtyFields.allowManualPayments && agreementChangeModel.makeManualPaymentsChange(allowManualPayments),
                formState.dirtyFields.coverCreditCardFees && agreementChangeModel.makeCoverCreditCardFeesChange(coverCreditCardFees),
                customPagesAreDirty && agreementChangeModel.makeCustomPagesChange(fullCustomPagesForSubmit),
                ...makeServiceQboClassChanges(services, relationship.services, qboClasses)
            ]),
            onSuccess: async ()=>{
                await handlePromise(onUpdate());
                dismissModal(undefined, {
                    force: true
                });
            }
        }));
    }
    function onServiceChange(serviceId, updater) {
        const currentServices = getValues('services');
        const idx = currentServices.findIndex((s)=>s.id === serviceId);
        if (idx === -1) return;
        const nextService = updater(currentServices[idx]);
        update(idx, nextService);
        trigger("services.".concat(idx));
        return nextService;
    }
    function onRemoveService(serviceId) {
        const idx = services.findIndex((s)=>s.id === serviceId);
        remove(idx);
    }
    async function handleServicesAdded(newServices) {
        newServices.forEach((s)=>{
            logBI(11004, {
                id: s.id,
                name: s.name
            });
        });
        append(newServices);
        scrollToServiceId(newServices[newServices.length - 1].id);
    }
    function scrollToServiceId(serviceId) {
        const serviceEl = servicesRef.current[serviceId];
        if (!serviceEl) {
            if (scrollToServiceCounter.current < 20) {
                scrollToServiceCounter.current++;
                setTimeout(()=>scrollToServiceId(serviceId), 50);
            }
        } else {
            scrollToServiceCounter.current = 1;
            scrollIntoView(serviceEl, {
                disableScrollAnimation
            });
        }
    }
    function onInvalidSubmit(errors) {
        if (Array.isArray(errors.services)) {
            const firstServiceIdx = errors.services.findIndex((e)=>!!e);
            firstServiceIdx > -1 && scrollToServiceId(services[firstServiceIdx].id);
            return;
        }
        const errorMessageElement = document.querySelector('[aria-invalid="true"]');
        if (errorMessageElement) {
            scrollIntoView(errorMessageElement, {
                disableScrollAnimation
            });
        }
    }
    if (legalTermsError) {
        throw makeSentryIgnoredError('legalTermsError');
    }
    const getIsInvalid = useStateInRef(isInvalid);
    const validateService = useCallback((service)=>{
        return !getIsInvalid()(serviceModel.applyChanges(service), {
            relationshipEffectiveDate: relationship.effectiveDate ? fromDateStampObject(relationship.effectiveDate) : null,
            isTemplate: false
        });
    }, [
        relationship.effectiveDate
    ]);
    const handleSaveClick = (e)=>{
        if (customPagesAreDirty && customPages.some(isCustomPageContentInvalid)) {
            setSubmittedCustomPageIds(new Set(customPages.map((p)=>p.id)));
            if (customPagesRef.current != null) {
                scrollIntoView(customPagesRef.current, {
                    disableScrollAnimation
                });
            }
            return;
        }
        handleSubmit(onSubmit, onInvalidSubmit)(e);
    };
    return /*#__PURE__*/ _jsx(FullPageModal, {
        contentClassName: styles.content,
        testId: "edit-agr",
        onClose: onClose,
        children: /*#__PURE__*/ _jsxs(FormProvider, _object_spread_props(_object_spread({}, form), {
            children: [
                /*#__PURE__*/ _jsx(FullPageModal.HeaderStart, {
                    children: /*#__PURE__*/ _jsxs("div", {
                        "data-testid": "edit-agr-title",
                        children: [
                            "Edit agreement with ",
                            relationship.customer.name
                        ]
                    })
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: styles.centeredContent,
                    children: [
                        /*#__PURE__*/ _jsxs(Callout, {
                            testId: "approval-callout",
                            "data-type": approvalCalloutText.type,
                            children: [
                                approvalCalloutText.text,
                                ' ',
                                /*#__PURE__*/ _jsx(ExternalLinkButton, {
                                    bi: {
                                        name: 'edit_agreement_auto_approval_learn_more'
                                    },
                                    openInNewTab: true,
                                    underline: "hover",
                                    href: autoApprovalHelpUrl,
                                    testId: "approval-help",
                                    children: "Learn more"
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(Section, {
                            title: editAgreementTexts.servicesProvided(),
                            children: /*#__PURE__*/ _jsx(ServicesListStructure, {
                                agreementData: agreementData,
                                addServicesTestId: "add-services",
                                onServicesAdded: handleServicesAdded,
                                children: services.map((s, idx)=>{
                                    const originalService = originalServicesMap.get(s.id);
                                    const serviceAlreadyTerminated = serviceModel.isTerminated(s.status) && !!originalService && serviceModel.isTerminated(originalService.status);
                                    return !serviceModel.isCanceled(s.status) && !serviceModel.isCompleted(s.status) && !serviceAlreadyTerminated && /*#__PURE__*/ _jsx(Controller, {
                                        control: control,
                                        name: "services.".concat(idx),
                                        rules: {
                                            validate: validateService
                                        },
                                        render: (param)=>{
                                            let { fieldState, formState } = param;
                                            return /*#__PURE__*/ _jsx(EditAgreementService, {
                                                service: s,
                                                idx: idx,
                                                testId: getTestPrefix(idx),
                                                ref: (el)=>servicesRef.current[s.id] = el,
                                                relationship: relationship,
                                                agreementData: agreementData,
                                                originalService: originalService,
                                                showServiceValidationWarning: !!fieldState.error && formState.isSubmitted,
                                                onServiceChange: onServiceChange,
                                                onRemoveService: onRemoveService
                                            });
                                        }
                                    }, s.id);
                                })
                            })
                        }),
                        CUSTOM_PAGES_AMENDMENTS && /*#__PURE__*/ _jsx(EditAgreementCustomPages, {
                            relationship: relationship,
                            sectionRef: customPagesRef,
                            submittedPageIds: submittedCustomPageIds,
                            onUploadingChange: setIsCustomPageUploading
                        }),
                        /*#__PURE__*/ _jsxs(Section, {
                            title: editAgreementTexts.paymentSettings(),
                            children: [
                                /*#__PURE__*/ _jsx(EditNetTerms, {
                                    relationship: relationship
                                }),
                                /*#__PURE__*/ _jsx(EditCCFees, {
                                    relationship: relationship
                                }),
                                /*#__PURE__*/ _jsx(EditAllowManualPayments, {
                                    relationship: relationship
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(Section, {
                            title: editAgreementTexts.agreementTerms(),
                            children: [
                                /*#__PURE__*/ _jsx(EditLegalTerms, {
                                    relationship: relationship
                                }),
                                /*#__PURE__*/ _jsx(Separator, {
                                    spacing: "none"
                                }),
                                /*#__PURE__*/ _jsx(EditAmendmentsApprovalMethod, {
                                    relationship: relationship
                                }),
                                /*#__PURE__*/ _jsx(EditInvoicesApprovalMethod, {
                                    relationship: relationship
                                })
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs(FullPageModal.MainActions, {
                    children: [
                        /*#__PURE__*/ _jsx(Button, {
                            bi: {
                                name: 'cancel_edit_agreement'
                            },
                            skin: "primaryOutline",
                            testId: "edit-agr-cancel",
                            onClick: onClose,
                            children: "Cancel"
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            bi: {
                                name: 'submit_edit_agreement'
                            },
                            disabled: !formState.isDirty || isCustomPageUploading,
                            testId: "edit-agr-submit",
                            onClick: handleSaveClick,
                            children: submitText
                        })
                    ]
                })
            ]
        }))
    });
}
